import { useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, IndianRupee, Pencil, Trash2, Printer, Plus, Paperclip, FileText, Download, Eye, MessageCircle, User, History, BellRing, PauseCircle, PlayCircle, Camera } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { AuthImage } from "@/components/AuthImage";
import { DateField } from "@/components/DateField";
import { ProductFields, emptyProduct, cleanProduct } from "@/components/ProductFields";
import api, { fmt, fmtDate, errText, undoToast, openInNewTab, BACKEND_URL } from "@/lib/api";

const STATUSES = ["New", "With Karigar", "Ready", "In Shop", "Delivered", "Cancelled"];

export default function OrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [payOpen, setPayOpen] = useState(false);
  const [payment, setPayment] = useState({ amount: "", remarks: "" });
  const [karigarDialog, setKarigarDialog] = useState(null);
  const [karigarForm, setKarigarForm] = useState({ karigar_id: "", expected_return: "", remarks: "" });
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ order_number: "", order_date: "", delivery_date: "", notes: "" });
  const [productDialog, setProductDialog] = useState(null);
  const pdfRef = useRef(null);
  const photoRef = useRef(null);
  const [photoTarget, setPhotoTarget] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  const uploadPhoto = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f || !photoTarget) return;
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/orders/${id}/products/${photoTarget}/photo`, fd);
      toast.success("Photo saved");
      refresh();
    } catch (err) {
      toast.error(errText(err));
    }
  };

  const removePhoto = async (pid) => {
    try {
      await api.delete(`/orders/${id}/products/${pid}/photo`);
      toast.success("Photo removed");
      setPhotoPreview(null);
      refresh();
    } catch {
      toast.error("Failed to remove photo");
    }
  };

  const { data: order, isLoading } = useQuery({ queryKey: ["order", id], queryFn: async () => (await api.get(`/orders/${id}`)).data });
  const { data: karigars } = useQuery({ queryKey: ["karigars"], queryFn: async () => (await api.get("/karigars")).data });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["order", id] });
    qc.invalidateQueries({ queryKey: ["orders"] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
    qc.invalidateQueries({ queryKey: ["karigars"] });
  };

  const updateProduct = useMutation({
    mutationFn: async ({ pid, body }) => (await api.put(`/orders/${id}/products/${pid}`, body)).data,
    onSuccess: () => { toast.success("Status updated"); refresh(); setKarigarDialog(null); },
    onError: (e) => toast.error(errText(e)),
  });

  const addPayment = useMutation({
    mutationFn: async () => (await api.post(`/orders/${id}/payments`, { amount: Number(payment.amount), remarks: payment.remarks })).data,
    onSuccess: () => { toast.success("Payment recorded"); refresh(); setPayOpen(false); setPayment({ amount: "", remarks: "" }); },
    onError: (e) => toast.error(errText(e)),
  });

  const updateOrder = useMutation({
    mutationFn: async () => (await api.put(`/orders/${id}`, editForm)).data,
    onSuccess: () => { toast.success("Order updated"); refresh(); setEditOpen(false); },
    onError: (e) => toast.error(errText(e)),
  });

  const deleteOrder = useMutation({
    mutationFn: async () => (await api.delete(`/orders/${id}`)).data,
    onSuccess: (data) => { undoToast("Order deleted", data.undo_id, refresh); refresh(); navigate("/orders"); },
    onError: (e) => toast.error(errText(e)),
  });

  const saveProduct = useMutation({
    mutationFn: async () => {
      const body = cleanProduct(productDialog.data);
      return productDialog.mode === "add"
        ? (await api.post(`/orders/${id}/products`, body)).data
        : (await api.put(`/orders/${id}/products/${productDialog.data.id}`, body)).data;
    },
    onSuccess: () => { toast.success(productDialog.mode === "add" ? "Product added" : "Product updated"); refresh(); setProductDialog(null); },
    onError: (e) => toast.error(errText(e)),
  });

  const deleteProduct = useMutation({
    mutationFn: async (pid) => (await api.delete(`/orders/${id}/products/${pid}`)).data,
    onSuccess: (data) => { undoToast("Product removed", data.undo_id, refresh); refresh(); },
    onError: (e) => toast.error(errText(e)),
  });

  const printSlip = () => {
    openInNewTab(async () => {
      const res = await api.get(`/orders/${id}/slip`, { responseType: "blob" });
      return URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    }).catch(() => toast.error("Failed to generate slip"));
  };

  const sendWhatsApp = () => {
    openInNewTab(async () => {
      const { data } = await api.get(`/orders/${id}/whatsapp`);
      let digits = (data.phone || "").replace(/\D/g, "");
      if (!digits) { toast.error("Customer has no phone number"); return null; }
      if (digits.length === 10) digits = "91" + digits;
      const slipUrl = `${BACKEND_URL}/api/s/${data.code}`;
      const items = order.products.filter((p) => p.status !== "Cancelled");
      const itemLines = items.map((p, i) => `  ${i + 1}. ${p.name}${p.quantity > 1 ? ` (×${p.quantity})` : ""} — ${fmt(p.customer_price * p.quantity)}`);
      const msg = [
        `*ANAMTA NOOR*`,
        `_Zari Handwork · Handcrafted Elegance_`,
        ``,
        `Dear *${order.customer_name}*,`,
        `Thank you for choosing us! Here are your order details:`,
        ``,
        `*Order No:* ${order.order_number}`,
        `*Order Date:* ${fmtDate(order.order_date)}`,
        order.delivery_date ? `*Delivery Date:* ${fmtDate(order.delivery_date)}` : null,
        ``,
        `*Your Items (${items.reduce((s, p) => s + (p.quantity || 1), 0)}):*`,
        ...itemLines,
        ``,
        `*Payment Summary*`,
        `  Total: ${fmt(order.total_amount)}`,
        `  Paid: ${fmt(order.advance)}`,
        order.remaining > 0 ? `  *Balance: ${fmt(order.remaining)}*` : `  *Fully Paid*`,
        ``,
        `*Order Slip (PDF):*`,
        slipUrl,
        ``,
        `Aapke order ki har update ke liye hum yahin hain. Shukriya!`,
        `— Anamta Noor`,
      ].filter((l) => l !== null).join("\n");
      return `https://wa.me/${digits}?text=${encodeURIComponent(msg)}`;
    }).catch(() => toast.error("Failed to open WhatsApp"));
  };

  const toggleHold = async () => {
    try {
      if (order.on_hold) {
        await api.put(`/orders/${id}`, { on_hold: false, hold_reason: "" });
        toast.success("Order resumed");
      } else {
        const reason = window.prompt("Hold reason:", "Customer Change Requested");
        if (reason === null) return;
        await api.put(`/orders/${id}`, { on_hold: true, hold_reason: reason || "Customer Change Requested" });
        toast.success("Order put on hold");
      }
      qc.invalidateQueries({ queryKey: ["order", id] });
      qc.invalidateQueries({ queryKey: ["orders"] });
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const sendReminder = () => {
    openInNewTab(async () => {
      const { data } = await api.get(`/orders/${id}/whatsapp`);
      let digits = (data.phone || "").replace(/\D/g, "");
      if (!digits) { toast.error("Customer has no phone number"); return null; }
      if (digits.length === 10) digits = "91" + digits;
      const slipUrl = `${BACKEND_URL}/api/s/${data.code}`;
      const msg = [
        `*ANAMTA NOOR*`,
        `_Zari Handwork · Handcrafted Elegance_`,
        ``,
        `Dear *${order.customer_name}*,`,
        `A gentle reminder regarding your pending balance:`,
        ``,
        `*Order No:* ${order.order_number}`,
        order.delivery_date ? `*Delivery Date:* ${fmtDate(order.delivery_date)}` : null,
        ``,
        `*Payment Summary*`,
        `  Total: ${fmt(order.total_amount)}`,
        `  Paid: ${fmt(order.advance)}`,
        `  *Pending Balance: ${fmt(order.remaining)}*`,
        ``,
        `Kindly clear the balance at your convenience.`,
        ``,
        `*Order Slip (PDF):*`,
        slipUrl,
        ``,
        `Thank you for choosing us!`,
        `— Anamta Noor`,
      ].filter((l) => l !== null).join("\n");
      return `https://wa.me/${digits}?text=${encodeURIComponent(msg)}`;
    }).catch(() => toast.error("Failed to open WhatsApp"));
  };

  const uploadPdfs = useMutation({
    mutationFn: async (files) => {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      return (await api.post(`/orders/${id}/attachments`, fd)).data;
    },
    onSuccess: () => { toast.success("PDF uploaded"); refresh(); },
    onError: (e) => toast.error(errText(e)),
  });

  const deleteAttachment = useMutation({
    mutationFn: async (aid) => (await api.delete(`/orders/${id}/attachments/${aid}`)).data,
    onSuccess: (data) => { undoToast("PDF deleted", data.undo_id, refresh); refresh(); },
    onError: (e) => toast.error(errText(e)),
  });

  const fetchAttachment = async (aid) => {
    const res = await api.get(`/orders/${id}/attachments/${aid}`, { responseType: "blob" });
    return new Blob([res.data], { type: "application/pdf" });
  };

  const viewPdf = (a) => {
    openInNewTab(async () => URL.createObjectURL(await fetchAttachment(a.id)))
      .catch(() => toast.error("Failed to open PDF"));
  };

  const downloadPdf = async (a) => {
    try {
      const url = URL.createObjectURL(await fetchAttachment(a.id));
      const link = document.createElement("a");
      link.href = url;
      link.download = a.filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Failed to download PDF"); }
  };

  const onStatusChange = (p, status) => {
    if (status === "With Karigar") {
      setKarigarForm({ karigar_id: p.karigar_id || "", expected_return: p.expected_return || "", remarks: p.remarks || "" });
      setKarigarDialog(p);
    } else {
      updateProduct.mutate({ pid: p.id, body: { status } });
    }
  };

  if (isLoading || !order) return <div className="h-64 rounded-xl bg-muted animate-pulse" />;

  return (
    <div className="space-y-6" data-testid="order-detail-page">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="icon" data-testid="back-button" onClick={() => navigate("/orders")}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3">
            <h1 className="font-heading text-2xl font-semibold tracking-tight">{order.order_number}</h1>
            <StatusBadge status={order.status} />
          </div>
          <p className="text-sm text-muted-foreground mt-0.5">
            {order.customer_name} · Ordered {fmtDate(order.order_date)} · Delivery {fmtDate(order.delivery_date)}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="print-slip-button" variant="outline" className="rounded-full active:scale-95 transition-transform" onClick={printSlip}>
            <Printer className="h-4 w-4 mr-1.5" /> Print Slip
          </Button>
          <Button data-testid="whatsapp-slip-button" variant="outline" className="rounded-full text-green-600 hover:text-green-700 active:scale-95 transition-transform" onClick={sendWhatsApp}>
            <MessageCircle className="h-4 w-4 mr-1.5" /> WhatsApp
          </Button>
          {order.remaining > 0 && (
            <Button data-testid="payment-reminder-button" variant="outline" className="rounded-full text-amber-500 hover:text-amber-600 active:scale-95 transition-transform" onClick={sendReminder}>
              <BellRing className="h-4 w-4 mr-1.5" /> Remind
            </Button>
          )}
          <Button data-testid="hold-toggle-button" variant="outline" className={`rounded-full active:scale-95 transition-transform ${order.on_hold ? "text-green-600 hover:text-green-700" : "text-orange-500 hover:text-orange-600"}`} onClick={toggleHold}>
            {order.on_hold ? <><PlayCircle className="h-4 w-4 mr-1.5" /> Resume</> : <><PauseCircle className="h-4 w-4 mr-1.5" /> Hold</>}
          </Button>
          <Button data-testid="edit-order-button" variant="outline" className="rounded-full active:scale-95 transition-transform" onClick={() => { setEditForm({ order_number: order.order_number || "", order_date: order.order_date || "", delivery_date: order.delivery_date || "", notes: order.notes || "" }); setEditOpen(true); }}>
            <Pencil className="h-4 w-4 mr-1.5" /> Edit
          </Button>
          <Button data-testid="delete-order-button" variant="outline" className="rounded-full text-red-500 hover:text-red-600 active:scale-95 transition-transform" onClick={() => window.confirm(`Delete order ${order.order_number}? This cannot be undone.`) && deleteOrder.mutate()}>
            <Trash2 className="h-4 w-4 mr-1.5" /> Delete
          </Button>
          <Button data-testid="add-payment-button" className="rounded-full active:scale-95 transition-transform" onClick={() => setPayOpen(true)}>
            <IndianRupee className="h-4 w-4 mr-1" /> Record Payment
          </Button>
        </div>
      </div>

      {order.on_hold && (
        <div data-testid="on-hold-banner" className="rounded-xl border border-orange-500/40 bg-orange-500/10 px-5 py-3.5 flex items-center gap-3">
          <PauseCircle className="h-5 w-5 text-orange-500 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-orange-500 uppercase tracking-wide">On Hold – {order.hold_reason || "Customer Change Requested"}</p>
            <p className="text-xs text-muted-foreground mt-0.5">Production paused. This status also appears on the printed order slip.</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Total", value: fmt(order.total_amount) },
          { label: "Paid", value: fmt(order.advance), cls: "text-green-600" },
          { label: "Remaining", value: fmt(order.remaining), cls: order.remaining > 0 ? "text-red-500" : "" },
          { label: "Profit (est.)", value: fmt(order.products.reduce((s, p) => s + (p.status !== "Cancelled" ? p.profit : 0), 0)), cls: "text-emerald-600" },
        ].map((s) => (
          <div key={s.label} className="rounded-xl border bg-card p-5">
            <p className={`font-heading text-xl font-semibold ${s.cls || ""}`}>{s.value}</p>
            <p className="text-xs lg:text-sm text-muted-foreground mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      {order.customer && (
        <div className="rounded-xl border bg-card" data-testid="customer-info-card">
          <div className="flex items-center gap-2 px-6 py-4 border-b">
            <User className="h-4 w-4 text-muted-foreground" />
            <h2 className="font-heading font-semibold">Customer Information</h2>
          </div>
          <div className="p-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-x-4 gap-y-4 text-sm">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Customer</p>
              <button data-testid="customer-name-link" className="font-medium text-amber-500 hover:underline text-left" onClick={() => navigate(`/customers/${order.customer_id}`)}>
                {order.customer.name || order.customer_name}
              </button>
            </div>
            <div><p className="text-xs text-muted-foreground mb-1">Phone</p><p className="font-medium">{order.customer.phone || "—"}</p></div>
            <div><p className="text-xs text-muted-foreground mb-1">Address</p><p className="font-medium truncate" title={order.customer.address}>{order.customer.address || "—"}</p></div>
            <div><p className="text-xs text-muted-foreground mb-1">Total Orders</p><p className="font-medium">{order.customer.total_orders}</p></div>
            <div><p className="text-xs text-muted-foreground mb-1">Business Value</p><p className="font-medium">{fmt(order.customer.business_value)}</p></div>
            <div><p className="text-xs text-muted-foreground mb-1">Pending Balance</p><p className={`font-medium ${order.customer.pending_balance > 0 ? "text-red-500" : ""}`}>{fmt(order.customer.pending_balance)}</p></div>
            <div><p className="text-xs text-muted-foreground mb-1">Completed Orders</p><p className="font-medium text-green-600">{order.customer.completed_orders}</p></div>
          </div>
        </div>
      )}

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-heading font-semibold text-lg">Products ({order.products.length})</h2>
          <Button data-testid="add-product-button" variant="outline" size="sm" className="rounded-full active:scale-95 transition-transform" onClick={() => setProductDialog({ mode: "add", data: emptyProduct() })}>
            <Plus className="h-3.5 w-3.5 mr-1" /> Add Product
          </Button>
        </div>
        {order.products.map((p) => (
          <div key={p.id} data-testid={`order-product-${p.id}`} className="rounded-xl border bg-card p-5 space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                {p.photo && (
                  <button type="button" data-testid={`product-photo-thumb-${p.id}`} className="shrink-0" onClick={() => setPhotoPreview(p)}>
                    <AuthImage src={`/orders/${id}/products/${p.id}/photo?v=${(p.photo.storage_path || "").slice(-8)}`} alt={p.name} className="h-14 w-14 rounded-lg object-cover border hover:opacity-80 transition-opacity" />
                  </button>
                )}
                <div>
                  <p className="font-medium">{p.name} <span className="text-muted-foreground font-normal">× {p.quantity}</span></p>
                  <p className="text-sm text-muted-foreground">
                    {p.category}{p.design_name && ` · ${p.design_name}`}{p.design_code && ` (${p.design_code})`}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge status={p.status} />
                <Select value={p.status} onValueChange={(v) => onStatusChange(p, v)}>
                  <SelectTrigger data-testid={`product-status-select-${p.id}`} className="w-40 h-8 text-xs rounded-lg"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
                <Button variant="ghost" size="icon" className={`h-8 w-8 ${p.photo ? "text-amber-500" : "text-muted-foreground"}`} title={p.photo ? "Change photo" : "Add photo"} data-testid={`product-photo-button-${p.id}`} onClick={() => { setPhotoTarget(p.id); photoRef.current?.click(); }}>
                  <Camera className="h-3.5 w-3.5" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`edit-product-${p.id}`} onClick={() => setProductDialog({ mode: "edit", data: { ...p, karigar_id: p.karigar_id || "" } })}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground" data-testid={`delete-product-${p.id}`} onClick={() => window.confirm(`Remove "${p.name}" from this order?`) && deleteProduct.mutate(p.id)}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 text-sm">
              {[
                ["Price", fmt(p.customer_price)], ["Fabric", fmt(p.fabric_cost)], ["Stitching", fmt(p.stitching_cost)],
                ["Karigar", fmt(p.karigar_cost)], ["Total Cost", fmt(p.total_cost)],
              ].map(([l, v]) => (
                <div key={l}><p className="text-xs text-muted-foreground">{l}</p><p className="font-medium">{v}</p></div>
              ))}
              <div>
                <p className="text-xs text-muted-foreground">Profit</p>
                <p className={`font-medium ${p.profit >= 0 ? "text-emerald-600" : "text-red-500"}`}>{fmt(p.profit)}</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground border-t pt-3">
              <span>Qty: <span className="text-foreground font-medium">{p.quantity}</span></span>
              <span>Location: <span className="text-foreground font-medium">{p.location}</span></span>
              {p.karigar_name && <span>Karigar: <span className="text-foreground font-medium">{p.karigar_name}</span></span>}
              {p.sent_date && <span>Sent: {fmtDate(p.sent_date)}</span>}
              {p.expected_return && <span>Expected: {fmtDate(p.expected_return)}</span>}
              {p.received_date && <span>Received: {fmtDate(p.received_date)}</span>}
              {order.delivery_date && <span>Expected Delivery: {fmtDate(order.delivery_date)}</span>}
              {p.status === "With Karigar" && p.sent_date && (() => {
                const days = Math.max(Math.round((new Date() - new Date(p.sent_date)) / 86400000), 0);
                return <span className={days > 7 ? "text-red-500 font-medium" : "text-amber-500 font-medium"}>Pending: {days} day{days !== 1 && "s"}</span>;
              })()}
              {p.remarks && <span>Remarks: {p.remarks}</span>}
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-xl border bg-card">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="font-heading font-semibold flex items-center gap-2"><Paperclip className="h-4 w-4 text-muted-foreground" /> PDF Attachments ({(order.attachments || []).length})</h2>
          <input ref={pdfRef} type="file" accept=".pdf,application/pdf" multiple className="hidden" data-testid="detail-pdf-input"
            onChange={(e) => { const fs = Array.from(e.target.files || []); if (fs.length) uploadPdfs.mutate(fs); e.target.value = ""; }} />
          <Button data-testid="upload-pdf-button" variant="outline" size="sm" className="rounded-full active:scale-95 transition-transform" disabled={uploadPdfs.isPending} onClick={() => pdfRef.current?.click()}>
            <Plus className="h-3.5 w-3.5 mr-1" /> {uploadPdfs.isPending ? "Uploading..." : "Upload PDF"}
          </Button>
        </div>
        {(order.attachments || []).length === 0 ? (
          <p className="px-6 py-6 text-sm text-muted-foreground">No PDFs attached.</p>
        ) : (
          <ul className="divide-y">
            {order.attachments.map((a) => (
              <li key={a.id} data-testid={`attachment-${a.id}`} className="px-6 py-3 flex items-center gap-3 text-sm">
                <FileText className="h-4 w-4 text-red-400 shrink-0" />
                <span className="flex-1 truncate font-medium">{a.filename}</span>
                <span className="text-xs text-muted-foreground shrink-0">{(a.size / 1024).toFixed(0)} KB</span>
                <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`view-pdf-${a.id}`} title="View" onClick={() => viewPdf(a)}>
                  <Eye className="h-3.5 w-3.5" />
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`download-pdf-${a.id}`} title="Download" onClick={() => downloadPdf(a)}>
                  <Download className="h-3.5 w-3.5" />
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground" data-testid={`delete-pdf-${a.id}`} title="Delete" onClick={() => window.confirm(`Delete "${a.filename}"?`) && deleteAttachment.mutate(a.id)}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="rounded-xl border bg-card" data-testid="order-timeline">
        <div className="flex items-center gap-2 px-6 py-4 border-b">
          <History className="h-4 w-4 text-muted-foreground" />
          <h2 className="font-heading font-semibold">Order Timeline</h2>
        </div>
        {(order.timeline || []).length === 0 ? (
          <p className="px-6 py-6 text-sm text-muted-foreground">No events recorded yet.</p>
        ) : (
          <ul className="px-6 py-4">
            {order.timeline.map((ev, i) => {
              const dot = {
                "Payment Received": "bg-green-500", "Sent To Karigar": "bg-orange-500",
                "Order Created": "bg-blue-500", "Cancelled": "bg-red-500",
                "Received From Karigar": "bg-teal-500", "Delivered": "bg-green-500",
                "PDF Uploaded": "bg-violet-500", "PDF Removed": "bg-zinc-500",
              }[ev.event] || "bg-amber-500";
              const dt = ev.created_at ? new Date(ev.created_at) : null;
              return (
                <li key={ev.id || i} className="relative pl-6 pb-5 last:pb-1">
                  {i < order.timeline.length - 1 && <span className="absolute left-[5px] top-4 bottom-0 w-px bg-border" />}
                  <span className={`absolute left-0 top-1.5 h-2.5 w-2.5 rounded-full ${dot}`} />
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
                    <p className="text-sm font-medium">{ev.event}</p>
                    {ev.details && <p className="text-xs text-muted-foreground">{ev.details}</p>}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {dt && !isNaN(dt) ? dt.toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : ev.created_at}
                    {ev.user && ` · ${ev.user}`}
                  </p>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="rounded-xl border bg-card">
        <h2 className="font-heading font-semibold px-6 py-4 border-b">Payments</h2>
        {order.payments.length === 0 ? (
          <p className="px-6 py-6 text-sm text-muted-foreground">No payments yet.</p>
        ) : (
          <ul className="divide-y">
            {order.payments.map((p) => (
              <li key={p.id} className="px-6 py-3 flex justify-between text-sm">
                <span>{fmtDate(p.date)} {p.remarks && <span className="text-muted-foreground">· {p.remarks}</span>}</span>
                <span className="font-medium text-green-600">{fmt(p.amount)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Record Payment</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Amount ₹ (Remaining: {fmt(order.remaining)})</Label>
              <Input data-testid="payment-amount-input" type="number" min="0" value={payment.amount} onChange={(e) => setPayment({ ...payment, amount: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Remarks</Label>
              <Input data-testid="payment-remarks-input" value={payment.remarks} onChange={(e) => setPayment({ ...payment, remarks: e.target.value })} />
            </div>
            <Button data-testid="payment-save-button" className="w-full rounded-lg" disabled={!Number(payment.amount) || addPayment.isPending} onClick={() => addPayment.mutate()}>
              Save Payment
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!karigarDialog} onOpenChange={(o) => !o && setKarigarDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Send to Karigar</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Karigar *</Label>
              <Select value={karigarForm.karigar_id} onValueChange={(v) => setKarigarForm({ ...karigarForm, karigar_id: v })}>
                <SelectTrigger data-testid="karigar-select"><SelectValue placeholder="Select karigar" /></SelectTrigger>
                <SelectContent>{karigars?.map((k) => <SelectItem key={k.id} value={k.id}>{k.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Expected Return</Label>
              <DateField testId="expected-return-field" value={karigarForm.expected_return} onChange={(v) => setKarigarForm({ ...karigarForm, expected_return: v })} />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Remarks</Label>
              <Input data-testid="karigar-remarks-input" value={karigarForm.remarks} onChange={(e) => setKarigarForm({ ...karigarForm, remarks: e.target.value })} />
            </div>
            <Button
              data-testid="send-to-karigar-button"
              className="w-full rounded-lg"
              disabled={!karigarForm.karigar_id || updateProduct.isPending}
              onClick={() => updateProduct.mutate({ pid: karigarDialog.id, body: { status: "With Karigar", ...karigarForm } })}
            >
              Send to Karigar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Edit Order</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Order Number</Label>
              <Input data-testid="edit-order-number-input" value={editForm.order_number} onChange={(e) => setEditForm({ ...editForm, order_number: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Order Date</Label>
                <DateField testId="edit-order-date-field" value={editForm.order_date} onChange={(v) => setEditForm({ ...editForm, order_date: v })} />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Delivery Date</Label>
                <DateField testId="edit-delivery-date-field" value={editForm.delivery_date} onChange={(v) => setEditForm({ ...editForm, delivery_date: v })} />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Notes</Label>
              <Input data-testid="edit-order-notes-input" value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} />
            </div>
            <Button data-testid="edit-order-save-button" className="w-full rounded-lg" disabled={updateOrder.isPending} onClick={() => updateOrder.mutate()}>
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!productDialog} onOpenChange={(o) => !o && setProductDialog(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{productDialog?.mode === "add" ? "Add Product" : "Edit Product"}</DialogTitle></DialogHeader>
          {productDialog && (
            <div className="space-y-4 pt-2">
              <ProductFields value={productDialog.data} karigars={karigars} onChange={(v) => setProductDialog({ ...productDialog, data: v })} />
              <Button
                data-testid="product-dialog-save-button"
                className="w-full rounded-lg"
                disabled={!productDialog.data.name.trim() || saveProduct.isPending}
                onClick={() => saveProduct.mutate()}
              >
                {productDialog.mode === "add" ? "Add Product" : "Save Product"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <input ref={photoRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid="product-photo-input" onChange={uploadPhoto} />

      <Dialog open={!!photoPreview} onOpenChange={(v) => !v && setPhotoPreview(null)}>
        <DialogContent className="max-w-2xl" data-testid="product-photo-preview-dialog">
          <DialogHeader><DialogTitle className="font-heading">{photoPreview?.name}</DialogTitle></DialogHeader>
          {photoPreview && (
            <>
              <AuthImage src={`/orders/${id}/products/${photoPreview.id}/photo?v=${(photoPreview.photo?.storage_path || "").slice(-8)}`} alt={photoPreview.name} className="w-full max-h-[65vh] object-contain rounded-lg" />
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" className="rounded-lg" data-testid="product-photo-change-button" onClick={() => { setPhotoTarget(photoPreview.id); photoRef.current?.click(); }}>
                  <Camera className="h-3.5 w-3.5 mr-1.5" /> Change
                </Button>
                <Button variant="outline" size="sm" className="rounded-lg text-red-500 hover:text-red-600" data-testid="product-photo-remove-button" onClick={() => window.confirm("Remove this photo?") && removePhoto(photoPreview.id)}>
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Remove
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
